import { useState, useRef, useEffect, useId } from 'react';
import { Info } from 'lucide-react';

// Click/tap-to-toggle info popover -- NOT hover-only. The native `title`
// attribute this replaces app-wide has two real failures found live during
// a design audit (2026-09-11): on desktop it needs a slow, easy-to-miss
// hover dwell and renders as tiny unstyled browser chrome; on mobile it is
// completely unreachable, since touch has no hover state at all. Every one
// of these was explaining the app's own core jargon (credibility score,
// group average, creator tier) -- exactly the content a new/mobile user
// most needs and could least reach under the old pattern.
const InfoTooltip = ({ text, label, className = '', panelClassName = '', align = 'left' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <span ref={rootRef} className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        aria-label={label || text}
        className="text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          id={id}
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          className={`absolute z-50 top-full mt-2 w-64 max-w-[80vw] p-3 rounded-xl text-xs leading-relaxed
            bg-white text-gray-700 border border-gray-200 shadow-xl
            dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700
            ${align === 'right' ? 'right-0' : 'left-0'} ${panelClassName}`}
        >
          {text}
        </div>
      )}
    </span>
  );
};

export default InfoTooltip;
